/**
 * แปลง token ดิบที่มาจากชื่อไฟล์ parquet ให้เป็นข้อความที่คนอ่านรู้เรื่อง
 *
 * ต้นทางของ label คือ `display_name` / `display_names` ใน spec.toml ของแต่ละ template
 * ซึ่งถูก dump ทั้งก้อนเป็น spec.json (commands/misc.py `export_spec`) แล้ว MainInterface
 * fetch มาให้ Chart ผ่าน `setSpec` อยู่แล้ว — ที่นี่แค่อ่านของที่มีอยู่ ไม่ได้สร้าง mapping ใหม่
 *
 * ทุกฟังก์ชันคืน "ข้อความสำหรับแสดงเท่านั้น" ห้ามนำผลลัพธ์ไปใช้เป็น <option value>,
 * ชื่อไฟล์ parquet, ชื่อคอลัมน์ SQL หรือ React key — identity ยังเป็น token ดิบเสมอ
 * และเมื่อ resolve ไม่ได้ (spec ยังไม่โหลด / dataset ไม่มี label) จะคืน token เดิม ไม่ throw
 */

/**
 * dim ที่ commands/gen_dag.py ใส่ให้เองระดับ framework จึงไม่เคยอยู่ใน spec.toml ของ dataset ไหน
 * (`dim_list = ["ym", "y"] + ...` และ `division_id` เมื่อ template เปิด division)
 */
const BUILTIN_DIM_LABELS: Record<string, string> = {
  y: "ปี",
  ym: "เดือน",
  ymd: "วัน",
  division_id: "หน่วยงาน",
};

/** ตัวเลือกว่างของ Group / Filter 2 ซึ่ง getRelatedItems คืนมาเป็น null */
export const NO_VALUE_LABEL = "— ไม่ระบุ —";

/** ต่อ key ไว้ท้ายเสมอ เพื่อให้ cross-check กับชื่อไฟล์ parquet ได้ */
const withKey = (label: string, key: string) => `${label} (${key})`;

const dimLabel = (spec: any, key: string): string | undefined =>
  spec?.[key]?.display_name ?? BUILTIN_DIM_LABELS[key];

/**
 * metric แบบ agg: `<field>__<method>` เช่น id__count, revenue_paid__sum
 *
 * ผ่าที่ `__` ตัวแรกเสมอ — ถ้าใช้ตัวสุดท้ายจะพัง เพราะ calc key อย่าง
 * revenue_paid__per_encounter จะถูกผ่าเป็น field=`revenue_paid__per` method=`encounter`
 *
 * `display_names` จับคู่กับ `agg` ด้วย "ตำแหน่ง" ไม่ใช่ชื่อ method
 * (กติกาเดียวกับ zip() ใน commands/convert_displays.py)
 */
const aggLabel = (spec: any, token: string): string | undefined => {
  const at = token.indexOf("__");
  if (at < 0) return undefined;

  const entry = spec?.[token.slice(0, at)];
  if (!Array.isArray(entry?.agg) || !Array.isArray(entry?.display_names)) return undefined;

  const index = entry.agg.indexOf(token.slice(at + 2));
  return index < 0 ? undefined : entry.display_names[index];
};

/** metric แบบ calc: ตัว key เองคือชื่อคอลัมน์ เช่น revenue_paid__per_encounter */
const calcLabel = (spec: any, token: string): string | undefined => {
  const entry = spec?.[token];
  return entry?.calc ? entry.display_name : undefined;
};

/**
 * ชื่อ dim แบบไม่มี key ต่อท้าย — ใช้กับหัวแกน / หัว legend / tooltip ที่พื้นที่จำกัด
 * คืน key เดิมถ้าไม่มี label
 */
export const dimName = (spec: any, key: string | null | undefined): string =>
  key ? dimLabel(spec, key) ?? key : "";

/** ชื่อ metric แบบไม่มี key ต่อท้าย — ใช้กับหัวแกน / tooltip */
export const metricName = (spec: any, token: string | null | undefined): string => {
  if (!token) return "";

  // status metric ต่อท้ายด้วย __diff (เช่น user_id__distinct__diff จาก gen_thumbnails)
  // ตัด suffix ออกก่อนแล้วค่อย resolve ตัวฐาน ให้ชื่อตรงกับ tab ที่ convert_displays.py สร้าง
  // (__cum ยังไม่เคยโผล่ใน token จริง — ความเป็น cum อยู่ที่ dimension ym__active/ym__usage
  //  แต่รองรับไว้เผื่อ pipeline เปลี่ยนวิธีตั้งชื่อ)
  let base = token;
  let suffix = "";
  for (const marker of ["__diff", "__cum"]) {
    if (base.endsWith(marker)) {
      base = base.slice(0, -marker.length);
      suffix = ` (${marker.slice(2)})`;
      break;
    }
  }

  const label = aggLabel(spec, base) ?? calcLabel(spec, base);
  return label ? label + suffix : token;
};

export const labelForDim = (spec: any, key: string | null | undefined): string => {
  if (!key) return NO_VALUE_LABEL;
  const name = dimName(spec, key);
  return name === key ? key : withKey(name, key);
};

export const labelForMetric = (spec: any, token: string | null | undefined): string => {
  if (!token) return NO_VALUE_LABEL;
  const name = metricName(spec, token);
  return name === token ? token : withKey(name, token);
};

/**
 * filter token คือ `<dimKey>__<value>` ที่ Algorithm.getRelatedItems ประกอบขึ้น
 * ค่ามี "-", ช่องว่าง หรือแม้แต่ "__" ได้ (เช่น age_group__25-39 (Adult)) จึงผ่าได้ครั้งเดียว
 */
export const labelForFilter = (spec: any, token: string | null | undefined): string => {
  if (!token) return NO_VALUE_LABEL;

  const at = token.indexOf("__");
  if (at < 0) return token;

  const label = dimLabel(spec, token.slice(0, at));
  return label ? `${label}: ${token.slice(at + 2)}` : token;
};

/** filter ที่เป็นช่วงเวลา ต้องเรียงตามค่าจริง ไม่ใช่ตามข้อความที่แสดง */
const isTimeToken = (token: string) => /^(y|ym|ymd)__/.test(token);

/**
 * เรียงตัวเลือกใน dropdown ตาม label ที่ผู้ใช้เห็นจริง
 *
 * Algorithm.selectable() เรียงจาก token ดิบ ซึ่งพอเปลี่ยนไปแสดง label แล้วลำดับจะดูมั่ว
 * (เช่น "จำนวน Encounter" มาก่อน "จำนวนผู้ใช้งาน" เพราะ id < patient_id)
 * ไม่แก้ที่ Algorithm.ts เพราะเป็น pure function ที่ไม่รู้จัก spec
 */
export const sortOptionsByLabel = (
  items: any[],
  toLabel: (item: any) => string,
): any[] =>
  [...items].sort((left, right) => {
    // ตัวเลือกว่างต้องอยู่บนสุดเสมอ ผู้ใช้ใช้มันเพื่อล้างค่า
    if (left === null || left === undefined) return -1;
    if (right === null || right === undefined) return 1;

    const leftToken = String(left);
    const rightToken = String(right);
    if (isTimeToken(leftToken) && isTimeToken(rightToken)) {
      return leftToken < rightToken ? -1 : leftToken > rightToken ? 1 : 0;
    }

    return toLabel(left).localeCompare(toLabel(right), "th", {
      numeric: true,
      sensitivity: "base",
    });
  });
